import {
  CollaboratorAvatars,
  collaboratorPreviewName,
} from '~publish/components/Composer/InstagramPreview/CollaboratorAvatars'
import { useSplitEnabled } from '@buffer-mono/features'
import {
  instagramPlaybackKey,
  useInstagramMusicPlayback,
} from '../InstagramMusic/InstagramMusicPlaybackContext'
import type { InstagramMusicPlaybackControls } from '../InstagramMusic/InstagramMusicPlaybackContext'
import type { InstagramPreviewContent, InstagramPreviewProfile } from './types'
import * as React from 'react'

import { clsx } from 'clsx'

import {
  EllipsisIcon,
  FlaskConicalIcon,
  IgProductTagIcon,
  MapPinIcon,
  MusicIcon,
} from '@buffer-mono/popcorn'

import { PostFields } from '@buffer-mono/reminders-config'

import type { Video } from '~publish/legacy/composer/composer/entities/factories'
import { PostTypeReel } from '~publish/legacy/post/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseInstagramLinks } from '~publish/legacy/utils/channels/instagram/helpers'

import {
  AiInfoIcon,
  CommentIcon,
  LikeIcon,
  RepostIcon,
  ShareIcon,
} from '~publish/legacy/composer/composer/components/post-preview/assets/instagram'
import {
  EmptyMediaPlaceholder,
  PostWrapper,
  PreviewAiMarker,
} from '~publish/legacy/composer/composer/components/post-preview/previews/Common/styles'
import {
  DEFAULT_AVATAR,
  getOrientation,
  ImageOrientation,
} from '~publish/legacy/composer/composer/components/post-preview/previews/Common/utils'
import { InstagramMusicVideoPlayer } from '../InstagramMusic/InstagramMusicVideoPlayer'
import {
  isInstagramAudioPreviewUnavailable,
  useInstagramSelectedAudioPreview,
} from '../InstagramMusic/hooks/useInstagramSelectedAudioPreview'
import { AudioLinesPlayingIcon } from '../InstagramMusic/AudioLinesPlayingIcon'
import type { InstagramPreviewAudio } from '../InstagramMusic/types'
import { PreviewAvatar } from '~publish/legacy/composer/composer/components/post-preview/previews/InstagramPreview/components/PreviewAvatar'

import styles from '~publish/legacy/composer/composer/components/post-preview/previews/InstagramPreview/components/Reel/Reel.module.css'
import instagramStyles from '~publish/legacy/composer/composer/components/post-preview/previews/InstagramPreview/InstagramPreview.module.css'

import musicStyles from '../InstagramMusic/InstagramMusicReel.module.css'
import trialStyles from './Reel.module.css'

import type { JSX } from 'react'

const ImagePreview = ({ url }: { url: string }): React.JSX.Element => (
  <div className={styles.imageWrapper} data-testid="preview-images">
    <img className={styles.image} src={url} alt="Instagram post media" />
  </div>
)

const VideoPlayer = ({
  video,
  soundtrack,
  soundtrackSrc,
  onPlayingChange,
  onPlaybackErrorChange,
  playbackRef,
  auditionId,
  auditionAudio,
  onAuditionEnded,
  onAuditionError,
}: {
  video: Video
  auditionId?: string
  auditionAudio?: HTMLAudioElement
  onAuditionEnded?: () => void
  onAuditionError?: () => void
  soundtrack:
    | Pick<InstagramPreviewAudio, 'audio_volume' | 'video_volume'>
    | null
    | undefined
  soundtrackSrc: string | null
  onPlayingChange: (playing: boolean) => void
  onPlaybackErrorChange: (failed: boolean) => void
  playbackRef?: React.Ref<InstagramMusicPlaybackControls>
}): React.JSX.Element => {
  const orientation = getOrientation({
    height: video.height,
    width: video.width,
  })

  return (
    <InstagramMusicVideoPlayer
      video={video}
      auditionId={auditionId}
      auditionAudio={auditionAudio}
      onAuditionEnded={onAuditionEnded}
      onAuditionError={onAuditionError}
      playbackRef={playbackRef}
      soundtrackSrc={soundtrackSrc}
      hasSoundtrack={Boolean(soundtrack)}
      soundtrackVolume={soundtrack?.audio_volume}
      videoVolume={soundtrack?.video_volume}
      onPlayingChange={onPlayingChange}
      onPlaybackErrorChange={onPlaybackErrorChange}
      className={clsx(styles.videoPlayer, {
        [styles.portrait]: orientation === ImageOrientation.Portrait,
      })}
    />
  )
}

const AudioCredit = ({
  label,
  isPlaying,
}: {
  label: string
  isPlaying: boolean
}): React.JSX.Element => {
  return (
    <div className={musicStyles.audioCredit} data-testid="reel-audio-credit">
      {isPlaying ? (
        <AudioLinesPlayingIcon
          className={musicStyles.audioCreditIcon}
          isPlaying
        />
      ) : (
        <MusicIcon
          className={musicStyles.audioCreditIcon}
          size="xsmall"
          aria-hidden
        />
      )}
      <span
        className={musicStyles.audioCreditText}
        data-playing={isPlaying}
        title={label}
        translate="no"
      >
        <span className={musicStyles.audioCreditMarquee} key={label}>
          <span>{label}</span>
          {isPlaying && <span aria-hidden>{label}</span>}
        </span>
      </span>
    </div>
  )
}

function formatSelectedAudioCredit(
  audio: Pick<
    InstagramPreviewAudio,
    'audio_type' | 'title' | 'display_artist' | 'creator_username'
  >,
): string {
  if (audio.audio_type === 'original_sound') {
    return audio.creator_username
      ? `${audio.title} · ${audio.creator_username}`
      : audio.title
  }
  const artist = audio.display_artist || audio.creator_username
  return artist ? `${audio.title} · ${artist}` : audio.title
}

export const Reel = ({
  content,
  profile,
}: {
  content: InstagramPreviewContent
  profile: InstagramPreviewProfile
}): JSX.Element => {
  const { isReady, isEnabled } = useSplitEnabled('instagram-music')
  const selectedAudio =
    isReady && isEnabled && !content.isReminder && profile.channelId
      ? content.channelData?.instagram?.audio
      : null
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [playbackFailed, setPlaybackFailed] = React.useState(false)
  const preview = useInstagramSelectedAudioPreview(
    content.video ? selectedAudio : null,
    profile.channelId,
  )
  const playback = useInstagramMusicPlayback()
  const audition = playback?.audition
  const auditionId =
    profile.channelId &&
    content.video?.url &&
    audition?.videoKey ===
      instagramPlaybackKey(profile.channelId, '', content.video.url)
      ? audition.trackId
      : undefined
  const isSelectedAudioPlaying =
    isPlaying && (!auditionId || auditionId === selectedAudio?.audio_id)
  const register = playback?.register
  const report = playback?.report
  const stopPlayback = playback?.stop
  const player = React.useRef<InstagramMusicPlaybackControls>(null)
  const playbackKey =
    selectedAudio && profile.channelId && content.video?.url
      ? instagramPlaybackKey(
          profile.channelId,
          selectedAudio.audio_id,
          content.video.url,
        )
      : null
  React.useEffect(
    () => (): void => {
      if (playbackKey) stopPlayback?.(playbackKey)
    },
    [playbackKey, stopPlayback],
  )
  React.useEffect(() => {
    if (!playbackKey || !register || !preview.dataUrl) return
    return register(playbackKey, {
      toggle: () => player.current?.toggle(),
      pause: () => player.current?.pause(),
    })
  }, [playbackKey, register, preview.dataUrl])
  React.useEffect(() => {
    if (!playbackKey || !report) return
    report(
      playbackKey,
      preview.errorMessage
        ? isInstagramAudioPreviewUnavailable(preview.errorMessage)
          ? 'unavailable'
          : 'error'
        : playbackFailed
          ? 'error'
          : !preview.dataUrl
            ? 'loading'
            : isSelectedAudioPlaying
              ? 'playing'
              : 'idle',
    )
  }, [
    playbackKey,
    report,
    preview.errorMessage,
    preview.dataUrl,
    playbackFailed,
    isSelectedAudioPlaying,
  ])
  const selectedAudioCredit = selectedAudio
    ? formatSelectedAudioCredit(selectedAudio)
    : null
  const collaborators = content.isReminder
    ? []
    : (content.channelData?.instagram?.collaborators ?? [])
  const firstCollaborator = collaborators[0]
  const { avatar, username } = profile
  const { text, video, gif, isAiGenerated } = content

  let locationName =
    content.isReminder &&
    content.selectedStickers?.includes(PostFields.LOCATION_NAME)
      ? content?.channelData?.instagram?.service_geolocation_name
      : ''
  if (!content.isReminder) {
    locationName = content?.channelData?.instagram?.service_geolocation_name
  }
  const stickerProducts =
    content.isReminder &&
    content.selectedStickers?.includes(PostFields.PRODUCTS)
      ? content?.channelData?.instagram?.products
      : ''
  const stickerMusic =
    content.isReminder && content.selectedStickers?.includes(PostFields.MUSIC)
      ? content?.channelData?.instagram?.music
      : ''
  const stickerText =
    content.isReminder && content.selectedStickers?.includes(PostFields.TEXT)
      ? content?.channelData?.instagram?.text
      : ''

  return (
    <PostWrapper className={styles.postWrapper} data-testid="reels">
      {video && (
        <VideoPlayer
          video={video}
          playbackRef={playbackKey ? player : undefined}
          soundtrack={
            auditionId
              ? {
                  video_volume: audition?.videoVolume ?? 100,
                  audio_volume: 100,
                }
              : selectedAudio
          }
          soundtrackSrc={auditionId ? null : preview.dataUrl}
          auditionId={auditionId}
          auditionAudio={auditionId ? audition?.audio : undefined}
          onAuditionEnded={auditionId ? audition?.onEnded : undefined}
          onAuditionError={auditionId ? audition?.onError : undefined}
          onPlayingChange={setIsPlaying}
          onPlaybackErrorChange={setPlaybackFailed}
        />
      )}
      {gif && <ImagePreview url={gif.url} />}
      {!video && !gif && (
        <EmptyMediaPlaceholder service="instagram" className={styles.empty} />
      )}
      {!content.isReminder &&
        content.channelData?.instagram?.trial_reel_graduation_strategy && (
          <span className={trialStyles.trialPill}>
            <FlaskConicalIcon size="small" />
            Trial
          </span>
        )}
      {stickerText && (
        <div className={instagramStyles.textStickerWrapper}>
          <span className={instagramStyles.textStickerContent} translate="no">
            {stickerText}
          </span>
        </div>
      )}
      <div className={styles.metadataWrapper}>
        <div
          className={
            selectedAudioCredit
              ? clsx(musicStyles.user, musicStyles.userWithAudio)
              : styles.user
          }
        >
          {firstCollaborator ? (
            <CollaboratorAvatars
              avatar={avatar}
              username={username}
              collaborator={firstCollaborator}
            />
          ) : (
            <PreviewAvatar
              className={
                selectedAudioCredit
                  ? musicStyles.avatar
                  : instagramStyles.avatar
              }
              data-testid="avatar"
              src={avatar}
              fallbackUrl={DEFAULT_AVATAR}
            />
          )}
          <div
            className={
              selectedAudioCredit
                ? musicStyles.usernameWrapper
                : styles.usernameWrapper
            }
          >
            <span
              className={
                selectedAudioCredit ? musicStyles.username : styles.username
              }
              translate="no"
            >
              {collaboratorPreviewName(username, collaborators.length)}
            </span>
            {isAiGenerated && (
              <PreviewAiMarker
                icon={<AiInfoIcon />}
                label="AI info"
                className={styles.aiMarker}
              />
            )}
            {selectedAudioCredit && (
              <AudioCredit
                label={selectedAudioCredit}
                isPlaying={isSelectedAudioPlaying}
              />
            )}
          </div>
        </div>
        {text && (
          <div className={styles.caption}>
            <LinkifiedText
              className={styles.linkifiedText}
              links={parseInstagramLinks(PostTypeReel, text)}
              size="mini"
              whitespace="pre-wrap"
              newTab
            >
              {text}
            </LinkifiedText>
          </div>
        )}
        <div className={styles.extraData}>
          {stickerMusic && (
            <div className={styles.extraDataItem}>
              <MusicIcon color="inverted" />
              <div className={styles.wrappedText}>
                <span translate="no">{username}</span> · Original Audio
              </div>
            </div>
          )}
          {stickerProducts && (
            <div className={styles.extraDataItem}>
              <div className={styles.wrappedText}>
                <IgProductTagIcon color="inverted" />
                Product
              </div>
            </div>
          )}
          {locationName && (
            <div className={styles.location}>
              <MapPinIcon className={styles.locationPin} />
              <div className={styles.wrappedText} translate="no">
                {locationName}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.iconsSideSection}>
        <LikeIcon size={24} color="white" />
        <CommentIcon size={24} color="white" />
        <RepostIcon size={24} color="white" />
        <ShareIcon size={24} color="white" />
        <EllipsisIcon size={24} color="inverted" />
        <PreviewAvatar
          className={styles.avatarSquare}
          data-testid="avatar-square"
          src={avatar}
          fallbackUrl={DEFAULT_AVATAR}
        />
      </div>
    </PostWrapper>
  )
}
